/**
 * Controles del reproductor: barra de progreso arriba, píldora con capítulo +
 * pausar / siguiente / salir (aparece al mover el mouse, siempre en pausa) y
 * el aviso permanente de "datos de demo".
 */
import { Pause, Play, SkipForward, X } from 'lucide-react';
import { AnimatePresence, motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import type { EngineState } from './engine';

interface PlayerControlsProps {
  state: EngineState;
  chapterTitle: string;
  chapters: number;
  visible: boolean;
  onTogglePause: () => void;
  onNext: () => void;
  onExit: () => void;
}

const iconBtn = 'inline-flex h-8 w-8 items-center justify-center rounded-full text-white/80 transition-colors hover:bg-white/15 hover:text-white';

export function PlayerControls({ state, chapterTitle, chapters, visible, onTogglePause, onNext, onExit }: PlayerControlsProps) {
  const { t } = useTranslation();
  const paused = state.status === 'paused';
  const show = visible || paused;
  return (
    <>
      <div className="pointer-events-none fixed inset-x-0 top-0 z-205 h-[3px] bg-white/10">
        <motion.div className="h-full bg-brand-green" animate={{ width: `${Math.round(state.progress * 100)}%` }} transition={{ duration: 0.4 }} />
      </div>

      <AnimatePresence>
        {show && state.status !== 'ended' && (
          <motion.div
            className="fixed right-4 top-3 z-205 flex items-center gap-1 rounded-full bg-[#0b1513]/92 py-1 pl-4 pr-1.5 text-sm text-white shadow-lg backdrop-blur-sm"
            initial={{ opacity: 0, y: -6 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -6 }}
            transition={{ duration: 0.18 }}
          >
            <span className="mr-2 whitespace-nowrap">
              <span className="text-brand-green">{state.chapter + 1}/{chapters}</span>
              <span className="text-white/60"> · </span>
              {chapterTitle}
            </span>
            <button type="button" className={iconBtn} onClick={onTogglePause} title={paused ? t('vault.workshop.resume', 'Reanudar') : t('vault.workshop.pause', 'Pausar')} aria-label={paused ? t('vault.workshop.resume', 'Reanudar') : t('vault.workshop.pause', 'Pausar')}>
              {paused ? <Play className="h-4 w-4" /> : <Pause className="h-4 w-4" />}
            </button>
            <button type="button" className={iconBtn} onClick={onNext} title={t('vault.workshop.next', 'Siguiente capítulo')} aria-label={t('vault.workshop.next', 'Siguiente capítulo')}>
              <SkipForward className="h-4 w-4" />
            </button>
            <button type="button" className={iconBtn} onClick={onExit} title={t('vault.workshop.exit', 'Salir del workshop')} aria-label={t('vault.workshop.exit', 'Salir del workshop')}>
              <X className="h-4 w-4" />
            </button>
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {paused && (
          <motion.div
            className="pointer-events-none fixed inset-x-0 top-14 z-205 flex justify-center"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <span className="rounded-full bg-[#0b1513]/85 px-3 py-1 text-xs text-white/80 backdrop-blur-sm">
              {t('vault.workshop.paused_hint', 'En pausa · Espacio reanuda · → siguiente capítulo · Esc sale')}
            </span>
          </motion.div>
        )}
      </AnimatePresence>

      <div className="pointer-events-none fixed bottom-3 left-4 z-203 rounded-full border border-brand-green/40 bg-[#0b1513]/85 px-3 py-1 text-[11px] font-medium uppercase tracking-wider text-brand-green backdrop-blur-sm">
        {t('vault.workshop.demo_badge', 'Workshop · datos de demo')}
      </div>
    </>
  );
}
